import { ChangeDetectorRef, Injectable } from '@angular/core';
import { Dataset, DatasetSubtype, DatasetType, DatasetsSet } from 'src/app/modules/core/store/models/dataset.model';
import { RelatedDocumentCardsSection, RelatedDocumentsView } from '../related-ducument-model';
import { FieldDataType, RelatedDocument } from 'src/app/modules/management/store/data/data.model';
import { produce } from 'immer';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ListQuery, providerIconLookup } from 'src/app/modules/cop/store/lists/models/card.model';
import { Filter, FilterGroup, FilterGroupType, RuleOperator } from '@app/modules/core/store/models/filters.model';
import { Document, QueryParams, RelatedDocumentCountsByDataset, RelatedDocumentsRequest } from 'src/app/modules/core/store/models';
import { v4 as uuidv4 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { Layer } from '@app/modules/cop/store/cop/models';

@Injectable({
  providedIn: 'root'
})
export class RelatedViewsService {
  constructor(private datasetApiService: DatasetApiService, private translateService: TranslateService) {}

  private defaultRelatedOptions: any[] = [
    {
      type: DatasetType.Alerts,
      subtype: DatasetSubtype.Intelligence,
      template: '{summary}',
      showZoom: true
    },
    {
      type: DatasetType.Incident,
      subtype: DatasetSubtype.None,
      template: '{number}:{name} {summary}',
      showZoom: true
    }
  ];

  public GetDefaultRelatedOption(type: DatasetType, subtype: DatasetSubtype): any {
    return this.defaultRelatedOptions.find(d => d.type === type && d.subtype === subtype);
  }

  public buildRelatedListQuery(
    relatedCounts: RelatedDocumentCountsByDataset[],
    fromDataset: Dataset,
    fromDocument: Document,
    datasetsSet: DatasetsSet,
    layersSet: { [layerUId: string]: Layer }
  ): Observable<ListQuery> {
    const layers = Object.values(layersSet ?? {});
    const relatedQuery: ListQuery = {
      updateDate: new Date().getTime().toString(),
      dataUpdateTime: {},
      data: {
        type: null,
        headerLabel: this.translateService.instant('EDIT-PANEL.RELATED-ITEMS'),
        colorCode: '',
        layerQueries: {},
        relatedDocumentsRequests: []
      }
    };

    const referenceFields = fromDataset?.schema?.fields?.filter(
      f => f.dataType === FieldDataType.ReferenceCriteria && !!f.settings?.targetDatasetUId
    );

    // from reference criteria fields
    referenceFields?.forEach(f => {
      const relatedRequest: RelatedDocumentsRequest = {
        uId: uuidv4(),
        fromDataset: fromDataset,
        fromDocument: fromDocument,
        targetDatasetUId: f.settings.targetDatasetUId,
        targetLayer: !!layers ? layers.find(l => l.datasetUId === f.settings.targetDatasetUId) : null,
        targetFilterGroup: !!f.settings.filterGroup
          ? this.convertReferenceFilterToRegularFilter(f.settings.filterGroup, fromDocument)
          : null,
        targetRefField: f
      };
      relatedQuery.data.relatedDocumentsRequests.push(relatedRequest);
      relatedQuery.data.layerQueries[relatedRequest.uId] = {
        query: {
          filterGroup: {
            filterGroups: [],
            filters: [],
            groupType: FilterGroupType.And
          },
          fields: null,
          pagination: { skip: 0, take: 100 }
        }
      };
    });

    // from releated counts
    relatedCounts?.forEach(c => {
      const relatedRequest: RelatedDocumentsRequest = {
        uId: uuidv4(),
        fromDataset: fromDataset,
        fromDocument: fromDocument,
        targetDatasetUId: c.targetDatasetUId,
        targetLayer: !!layers ? layers.find(l => l.datasetUId === c.targetDatasetUId) : null
      };
      relatedQuery.data.relatedDocumentsRequests.push(relatedRequest);
      relatedQuery.data.layerQueries[relatedRequest.uId] = {
        query: {
          filterGroup: {
            filterGroups: [],
            filters: [],
            groupType: FilterGroupType.And
          },
          fields: null,
          pagination: { skip: 0, take: 100 }
        }
      };
    });

    relatedQuery.data?.relatedDocumentsRequests.forEach(r => {
      if (!!datasetsSet[r.targetDatasetUId]) {
        r.targetDataset = datasetsSet[r.targetDatasetUId];
      }
    });

    const queryDatasetUIds = relatedQuery.data?.relatedDocumentsRequests.filter(d => !!!d.targetDataset).map(d => d.targetDatasetUId);

    if (queryDatasetUIds.length) {
      const queryParams: QueryParams = {
        skip: 0,
        take: queryDatasetUIds.length,
        sortField: 'Name',
        sortDirection: 'Ascending'
      };

      return this.datasetApiService.getDatasets(queryParams, true, true, queryDatasetUIds).pipe(
        take(1),
        map(res => {
          res?.data.forEach(r => {
            const request = relatedQuery.data?.relatedDocumentsRequests.find(q => q.targetDatasetUId === r.uId);
            if (!!request) {
              request.targetDataset = r;
            }
          });

          return relatedQuery;
        })
      );
    } else {
      return of(relatedQuery);
    }
  }

  public linkFilterHasDocumentFilter(linkFilterGroup: FilterGroup): boolean {
    if (!!!linkFilterGroup) {
      return false;
    }
    for (let filter of linkFilterGroup?.filters) {
      if (!!linkFilterGroup.toDatasetUId && !!filter.toSystemField) {
        return true;
      }
    }
    if (!!linkFilterGroup?.filterGroups) {
      linkFilterGroup.filterGroups.forEach(group => {
        if (this.linkFilterHasDocumentFilter(group) === true) {
          return true;
        }
      });
    }

    return false;
  }

  public convertReferenceFilterToRegularFilter(inputFilterGroup: FilterGroup, sourceDocument: Document): FilterGroup {
    if (!!!inputFilterGroup) {
      return null;
    }
    const processedFilterGroup: FilterGroup = {
      filterGroups: [],
      filters: [],
      groupType: inputFilterGroup.groupType
    };

    for (let filter of inputFilterGroup?.filters) {
      if (!inputFilterGroup.toDatasetUId) {
        processedFilterGroup.filters.push({
          field: filter.fromSystemField,
          value: filter.value,
          operator: filter.operator,
          options: filter.options,
          filterOptions: filter.filterOptions
        });
      } else if (!!filter.toSystemField) {
        try {
          const value = sourceDocument[filter.toSystemField];
          processedFilterGroup.filters.push({
            field: filter.fromSystemField,
            value,
            operator: filter.operator,
            options: filter.options,
            filterOptions: filter.filterOptions
          });
        } catch {}
      }
    }

    for (let group of inputFilterGroup.filterGroups) {
      processedFilterGroup.filterGroups.push(this.convertReferenceFilterToRegularFilter(group, sourceDocument));
    }

    return processedFilterGroup;
  }

  public getSectionIcon(dataset?: Dataset): {
    path?: string;
    iconBgColor?: string;
    iconFgColor?: string;
  } {
    let icon: string = null;
    let iconBgColor: string = null;
    let iconFgColor: string = null;

    if (dataset?.type === DatasetType.Alerts) {
      [icon, iconBgColor, iconFgColor] = providerIconLookup.find(p => p.name === 'notFound').img;
    } else if (dataset?.type === DatasetType.Incident) {
      [icon, iconBgColor, iconFgColor] = providerIconLookup.find(p => p.name === 'incident').img;
    }
    return {
      path: icon,
      iconBgColor: iconBgColor,
      iconFgColor: iconFgColor
    };
  }

  private addSectionDocuments(section: RelatedDocumentCardsSection, relatedDocuments: RelatedDocument[]): void {
    relatedDocuments
      .filter(r => r.datasetUId === section.dataset.uId)
      .forEach(r => {
        if (!!r.document) {
          section.cards.push({
            dataset: section.dataset,
            layer: section.layer,
            view: section.view,
            document: r.document
          });
        }
      });
  }

  private getDataset(datasetUId: string): Observable<any> {
    return this.datasetApiService.getDataset(datasetUId, { includeWorkflow: true }).pipe(
      catchError(e => {
        console.error(e);
        return of(null);
      }),
      map(data => {
        return data;
      })
    );
  }

  public getDatasetSortField(dataset: Dataset): string {
    const field = dataset.schema.fields.find(
      f => f.systemName === 'name' || f.systemName === 'summary' || f.systemName === 'subject' || f.systemName === 'display_name'
    );
    return !!field ? field.systemName : null;
  }

  public generateSearchClause(searchTerm: string, dataset: Dataset): FilterGroup {
    var clausePieces = [];
    var filters = [] as Filter[];

    if (!searchTerm) {
      return null;
    }

    dataset.schema.fields.forEach(f => {
      if (f.dataType === FieldDataType.Text || f.dataType === FieldDataType.AutoIncrement) {
        clausePieces.push(`${f.systemName}.ToLower().Contains("${searchTerm.replace('"', '\\"').toLocaleLowerCase()}")`);
        filters.push({
          field: f.systemName,
          operator: RuleOperator.ContainsNoCase,
          value: searchTerm
        });
      }
    });

    const searchFilterGroup =
      filters.length > 0
        ? {
            groupType: FilterGroupType.Or,
            filters: filters,
            filterGroups: []
          }
        : null;

    return searchFilterGroup;
  }
}
